/**
 * consent-store: framework-agnostic state, persistence, versioning, event bus,
 * Google Consent Mode v2 signals and GA4/GTM loading. No Solid imports here.
 */
import { CONSENT_EVENT, getConfig } from './config'

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing'

export interface ConsentChoices {
  necessary: true
  analytics: boolean
  marketing: boolean
}

/** What the user did (spec §8 `action`). */
export type ConsentAction = 'accept_all' | 'reject_all' | 'customize' | 'withdraw'
/** Which surface it happened on (spec §8 `source`). */
export type ConsentSurface = 'banner' | 'preferences_modal' | 'settings_link'

export interface StoredConsent {
  /** client-generated uuid v4, reused across updates on this browser */
  id: string
  /** consent schema version; a different value re-prompts the user */
  v: number
  /** policy version (config.ts POLICY_VERSION, final '2026-11-05') */
  pv: string
  /** ISO-8601 UTC timestamp of the choice */
  ts: string
  choices: ConsentChoices
  action: ConsentAction
  source: ConsentSurface
  /** navigator.globalPrivacyControl at the time of the choice */
  gpc: boolean
}

type GtagConsent = 'granted' | 'denied'
export interface ConsentModeState {
  ad_storage: GtagConsent
  analytics_storage: GtagConsent
  ad_user_data: GtagConsent
  ad_personalization: GtagConsent
  functionality_storage: GtagConsent
  personalization_storage: GtagConsent
  security_storage: GtagConsent
}

export const DENY_ALL: ConsentChoices = { necessary: true, analytics: false, marketing: false }
export const ACCEPT_ALL: ConsentChoices = { necessary: true, analytics: true, marketing: true }

const g = (b: boolean): GtagConsent => (b ? 'granted' : 'denied')

/**
 * Category -> Consent Mode v2 mapping (spec §3):
 *   necessary -> security_storage + functionality_storage (always granted)
 *   analytics -> analytics_storage
 *   marketing -> ad_storage, ad_user_data, ad_personalization, personalization_storage
 */
export function toConsentMode(c: ConsentChoices): ConsentModeState {
  return {
    analytics_storage: g(c.analytics),
    functionality_storage: 'granted',
    ad_storage: g(c.marketing),
    ad_user_data: g(c.marketing),
    ad_personalization: g(c.marketing),
    personalization_storage: g(c.marketing),
    security_storage: 'granted',
  }
}

export function isGpcEnabled(): boolean {
  try {
    return typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true
  } catch {
    return false
  }
}

// ---------- storage (localStorage primary, first-party cookie mirror) ----------

export function isValidStored(s: any, now = Date.now()): s is StoredConsent {
  const cfg = getConfig()
  if (!s || typeof s !== 'object' || s.v !== cfg.version || !s.choices || typeof s.ts !== 'string' || typeof s.id !== 'string')
    return false
  const t = Date.parse(s.ts)
  if (isNaN(t)) return false
  if (now - t > cfg.maxAgeDays * 864e5) return false
  return typeof s.choices.analytics === 'boolean' && typeof s.choices.marketing === 'boolean'
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

export function readStoredConsent(): StoredConsent | null {
  const cfg = getConfig()
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(cfg.storageKey)
  } catch {
    /* storage blocked */
  }
  if (!raw) raw = readCookie(cfg.cookieName)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return isValidStored(parsed) ? withGpc(parsed) : null
  } catch {
    return null
  }
}

/**
 * GPC is re-checked every time a stored state is read/re-applied: while the
 * browser sends GPC, marketing is never granted, whatever was stored.
 */
export function withGpc(s: StoredConsent): StoredConsent {
  if (!isGpcEnabled() || !s.choices.marketing) return s
  return { ...s, choices: { ...s.choices, marketing: false }, gpc: true }
}

/** Thrown by writeStoredConsent when the localStorage or cookie write throws. */
export class ConsentWriteError extends Error {
  constructor(
    public readonly target: 'localStorage' | 'cookie',
    public readonly cause?: unknown
  ) {
    super(`tune_consent: could not write the ${target}`)
    this.name = 'ConsentWriteError'
  }
}

/**
 * Write both copies (localStorage + cookie mirror). Both writes are attempted;
 * if either throws, a ConsentWriteError is thrown afterwards so the UI can show
 * COPY.saveError instead of reporting a save that did not happen.
 */
export function writeStoredConsent(s: StoredConsent): void {
  const cfg = getConfig()
  const raw = JSON.stringify(s)
  let failed: ConsentWriteError | null = null
  try {
    window.localStorage.setItem(cfg.storageKey, raw)
  } catch (e) {
    failed = new ConsentWriteError('localStorage', e)
  }
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${cfg.cookieName}=${encodeURIComponent(raw)}; Max-Age=${cfg.maxAgeDays * 86400}; Path=/; SameSite=Lax${secure}`
  } catch (e) {
    failed = failed || new ConsentWriteError('cookie', e)
  }
  if (failed) throw failed
}

export function clearStoredConsent(): void {
  const cfg = getConfig()
  try {
    window.localStorage.removeItem(cfg.storageKey)
  } catch {}
  try {
    document.cookie = `${cfg.cookieName}=; Max-Age=0; Path=/`
  } catch {}
}

// ---------- gtag / tag loading ----------

function gtag(...args: any[]) {
  const w: any = window
  w.dataLayer = w.dataLayer || []
  if (typeof w.gtag === 'function') w.gtag(...args)
  else w.dataLayer.push(arguments)
}

export function updateConsentMode(c: ConsentChoices): void {
  gtag('consent', 'update', toConsentMode(c))
}

function injectScript(src: string, id: string) {
  if (document.getElementById(id)) return
  const s = document.createElement('script')
  s.async = true
  s.src = src
  s.id = id
  document.head.appendChild(s)
}

/**
 * Inject GA4 / GTM only for granted categories. Idempotent and shared with
 * the inline bootstrap through window.__tuneTags. Tags cannot be "unloaded"
 * once injected; withdrawing consent sends a denied consent update (GA/GTM
 * stop writing cookies) and we clear GA cookies; a reload fully stops them.
 */
export function loadTags(c: ConsentChoices): void {
  const cfg = getConfig()
  if (!cfg.enableTags || typeof document === 'undefined') return
  const w: any = window
  const state = (w.__tuneTags = w.__tuneTags || { ga: false, gtm: false })
  if (c.analytics && !state.ga) {
    state.ga = true
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${cfg.gaId}`, 'tune-ga4')
    gtag('js', new Date())
    gtag('config', cfg.gaId)
  }
  if ((c.analytics || c.marketing) && !state.gtm) {
    state.gtm = true
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
    injectScript(`https://www.googletagmanager.com/gtm.js?id=${cfg.gtmId}`, 'tune-gtm')
  }
}

/** Analytics cookies (GA4). */
export const ANALYTICS_COOKIE_RE = /^(_ga($|_)|_gid$|_gat($|_)|_dc_gtm_)/
/** Marketing / ad cookies that GTM tags may set (Google Ads, Meta, TikTok, Bing, Pinterest, Reddit, Snap, LinkedIn). */
export const MARKETING_COOKIE_RE = /^(_gcl_|_gac_|_fbp$|_fbc$|_ttp$|_tt_|_uetsid|_uetvid|_pin_unauth$|_pinterest_|_rdt_|_scid|_sctr$|li_fat_id$|IDE$|test_cookie$)/

/** '', host, .host and every parent domain (.a.b.c -> .b.c), so cookies set on any level are removed. */
export function cookieDomainVariants(host = typeof location !== 'undefined' ? location.hostname : ''): string[] {
  const out = new Set<string>([''])
  if (!host) return ['']
  out.add(host)
  const parts = host.split('.')
  for (let i = 0; i < parts.length - 1; i++) out.add('.' + parts.slice(i).join('.'))
  return [...out]
}

function deleteCookiesMatching(re: RegExp): string[] {
  if (typeof document === 'undefined') return []
  const removed: string[] = []
  const domains = cookieDomainVariants()
  document.cookie.split(';').forEach((p) => {
    const name = p.split('=')[0].trim()
    if (!name || !re.test(name)) return
    removed.push(name)
    for (const d of domains)
      for (const path of ['/', location.pathname || '/'])
        document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}${d ? '; Domain=' + d : ''}`
  })
  return removed
}

/** Remove GA cookies after analytics consent is withdrawn. */
export function clearAnalyticsCookies(): string[] {
  return deleteCookiesMatching(ANALYTICS_COOKIE_RE)
}

/** Remove ad/marketing cookies after marketing consent is withdrawn. */
export function clearMarketingCookies(): string[] {
  return deleteCookiesMatching(MARKETING_COOKIE_RE)
}

// ---------- tiny store ----------

type Listener = (s: StoredConsent | null) => void
const listeners = new Set<Listener>()
let current: StoredConsent | null = null

export function getConsent(): StoredConsent | null {
  return current
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Load stored consent and apply it (idempotent with the inline bootstrap). */
export function initCore(): StoredConsent | null {
  current = readStoredConsent() // GPC already applied (withGpc)
  if (current) {
    if (!current.choices.marketing) clearMarketingCookies()
    updateConsentMode(current.choices)
    loadTags(current.choices)
  }
  return current
}

export function uuid4(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null
  if (c?.randomUUID) return c.randomUUID()
  const b = new Uint8Array(16)
  if (c?.getRandomValues) c.getRandomValues(b)
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/**
 * Persist a user choice, update Consent Mode, load tags (or clear GA cookies
 * on withdrawal), notify listeners, then optionally post a receipt.
 * `action` is upgraded to 'withdraw' when a previously granted category is revoked.
 */
/** Page reload used after a withdrawal; replaceable in tests. */
export let reloadPage: () => void = () => {
  try {
    window.location.reload()
  } catch {}
}
export function __setReloadForTests(fn: () => void) {
  reloadPage = fn
}
/** How long a withdrawal waits for the server receipt before reloading. */
export const RELOAD_POST_TIMEOUT_MS = 1200

/**
 * Tags already injected on this page (GA4 / GTM; `typekit` = the Adobe Fonts kit that
 * landing/_typekit_consent.html loads as a strictly necessary font and flags on the same object).
 */
export function loadedTags(): { ga: boolean; gtm: boolean; typekit: boolean } {
  const st = typeof window !== 'undefined' ? (window as any).__tuneTags : null
  return { ga: !!st?.ga, gtm: !!st?.gtm, typekit: !!st?.typekit }
}

/**
 * True when the new choice revokes a category whose tags are already running
 * on this page. GA4/GTM cannot be unloaded, so the page must reload.
 */
export function needsReload(prev: ConsentChoices | null | undefined, next: ConsentChoices): boolean {
  if (!prev) return false
  const t = loadedTags()
  const analyticsRevoked = prev.analytics && !next.analytics && (t.ga || t.gtm)
  // Adobe Fonts is strictly necessary (spec §4), so withdrawing marketing never reloads for it.
  const marketingRevoked = prev.marketing && !next.marketing && t.gtm
  return !!(analyticsRevoked || marketingRevoked)
}

export function saveConsent(
  choices: { analytics: boolean; marketing: boolean },
  action: Exclude<ConsentAction, 'withdraw'>,
  source: ConsentSurface,
  opts: { post?: (s: StoredConsent) => unknown } = {}
): StoredConsent {
  const prev = current
  const gpc = isGpcEnabled()
  // GPC = opt-out of sale/share: marketing can never be granted while it is on,
  // not even by "Accept all" (CCPA regs 7025; !3123 also forces it server-side).
  const c: ConsentChoices = { necessary: true, analytics: !!choices.analytics, marketing: !!choices.marketing && !gpc }
  const cfg = getConfig()
  const s: StoredConsent = {
    id: prev?.id || readAnyId() || uuid4(),
    v: cfg.version,
    pv: cfg.policyVersion,
    ts: new Date().toISOString(),
    choices: c,
    action: resolveAction(action, c, prev),
    source,
    gpc,
  }
  const reload = needsReload(prev?.choices, c)
  current = s
  // order: Consent Mode update first, then cookies, then storage, then reload
  updateConsentMode(c)
  if (prev?.choices.analytics && !c.analytics) clearAnalyticsCookies()
  if (!c.marketing) clearMarketingCookies()
  try {
    writeStoredConsent(s)
  } catch (e) {
    // Not saved: never report success, load tags, notify, post or reload.
    // Roll the in-memory state back; Consent Mode keeps only what both the old
    // and the new choice allow (a failed grant stays denied, a failed withdrawal
    // stays denied for this page), then the caller shows COPY.saveError.
    current = prev
    const was = prev?.choices || DENY_ALL
    updateConsentMode({ necessary: true, analytics: was.analytics && c.analytics, marketing: was.marketing && c.marketing })
    throw e
  }
  if (!reload) loadTags(c)
  try {
    ;(window as any).dataLayer?.push({ event: 'tune_consent_update', tune_consent: c })
  } catch {}
  listeners.forEach((l) => {
    try {
      l(s)
    } catch {}
  })
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: s }))
  } catch {}
  let posted: Promise<unknown> = Promise.resolve()
  if (opts.post) {
    try {
      posted = Promise.resolve(opts.post(s)).catch(() => {})
    } catch {}
  }
  if (reload) {
    // GA4/GTM are already running and cannot be unloaded: reload so they are
    // gone. The receipt is queued before sending (consent-api), so a reload
    // that interrupts it is retried on the next page.
    Promise.race([posted, new Promise((r) => setTimeout(r, RELOAD_POST_TIMEOUT_MS))]).then(() => reloadPage())
  }
  return s
}

/**
 * "Do Not Sell or Share My Personal Information" (CCPA, spec §4 ccpaLink):
 * marketing off, analytics kept as it is. Goes through saveConsent exactly like
 * a GPC opt-out (marketing forced false): denied ad_* Consent Mode update,
 * marketing cookies cleared on every domain, stored, and a reload when GTM is
 * already running. Throws (ConsentWriteError) when the choice cannot be stored.
 */
export function optOutOfSale(
  source: ConsentSurface,
  opts: { post?: (s: StoredConsent) => unknown } = {}
): StoredConsent {
  const analytics = !!current?.choices.analytics
  return saveConsent({ analytics, marketing: false }, 'customize', source, opts)
}

/**
 * Action that satisfies !3123's ConsentEventSerializer:
 *   accept_all  => analytics && marketing
 *   reject_all / withdraw => both false
 *   anything else => customize
 * 'withdraw' = everything optional off after something had been granted.
 */
export function resolveAction(
  requested: Exclude<ConsentAction, 'withdraw'>,
  c: ConsentChoices,
  prev: StoredConsent | null
): ConsentAction {
  const none = !c.analytics && !c.marketing
  if (none) return prev && (prev.choices.analytics || prev.choices.marketing) ? 'withdraw' : 'reject_all'
  if (requested === 'accept_all' && c.analytics && c.marketing) return 'accept_all'
  return 'customize'
}

/**
 * Adopt a state that came from the server (login sync). Applies it like a
 * user choice but does not post it back.
 */
export function applyServerConsent(server: {
  consent_id: string
  categories: { analytics: boolean; marketing: boolean }
  action: ConsentAction
  policy_version?: string
  received_at: string
}, source: ConsentSurface = 'settings_link'): StoredConsent {
  const prev = current
  const s = saveConsent(
    { analytics: server.categories.analytics, marketing: server.categories.marketing },
    server.action === 'accept_all' ? 'accept_all' : server.action === 'customize' ? 'customize' : 'reject_all',
    source
  )
  // keep the server's id (it is linked to the account) and its time
  const t = Date.parse(server.received_at)
  const merged: StoredConsent = { ...s, id: server.consent_id || s.id, ts: isNaN(t) ? s.ts : new Date(t).toISOString() }
  current = merged
  writeStoredConsent(merged)
  return merged
}

/**
 * The browser's consent_id (uuid v4) or null if the user never chose.
 * Reads storage directly, so it also works in the SPA bundle (which does not
 * mount the banner). Send it as `X-Consent-Id` on login so the backend can
 * link anonymous consent records to the account.
 */
export function getConsentId(): string | null {
  return current?.id || readStoredConsent()?.id || readAnyId()
}

/** `{ 'X-Consent-Id': id }` or `{}`; spread into request headers. */
export function consentIdHeaders(): Record<string, string> {
  const id = getConsentId()
  return id ? { 'X-Consent-Id': id } : {}
}

/** Keep the consent_id stable even when an old-version record is re-prompted. */
function readAnyId(): string | null {
  try {
    const raw = window.localStorage.getItem(getConfig().storageKey)
    const id = raw && JSON.parse(raw)?.id
    return typeof id === 'string' ? id : null
  } catch {
    return null
  }
}

/** Default toggle state for the banner: GPC => everything optional off. */
export function defaultChoices(): ConsentChoices {
  const s = current
  if (s) return { ...s.choices }
  return { ...DENY_ALL }
}

/** For tests only. */
export function __resetForTests() {
  current = null
  listeners.clear()
}
